import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Alert, Form, InputGroup, Modal } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useProjects, useDeleteProject } from '../../entities/project';
import { IconPlus, IconSearch } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { usePermissions } from '../../shared/permissions';
import { useSortPreference } from '../../shared/useSortPreference';
import { useDateFormat } from '../../shared/DateFormatContext';
import { CenteredLoader } from '../../components/LoadingSpinner';
import PageSection from '../../components/layout/PageSection';
import PageShell from '../../components/layout/PageShell';
import StatusBadge from '../../components/StatusBadge';
import AppButton from '../../components/ui/AppButton';
import AppTable from '../../components/ui/AppTable';

const PAGE_SIZE = 50;

// Map frontend sort keys to DRF ordering params
const ORDERING_MAP = {
  name: 'name',
  start_date: 'start_date',
  end_date: 'end_date',
  created_at: 'created_at',
  status: 'status__name',
  customer: 'customer__company_name',
};

function useDebounce(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

const Projects = () => {
  const { t } = useTranslation();
  const { canManage } = usePermissions();
  const { formatDate } = useDateFormat();
  const navigate = useNavigate();
  const sentinelRef = useRef(null);

  const [deleteModal, setDeleteModal] = useState({ show: false, project: null });
  const [bulkDeleteModal, setBulkDeleteModal] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useSortPreference('projects', { key: 'start_date', direction: 'desc' });

  const debouncedSearch = useDebounce(searchTerm, 300);

  const ordering = useMemo(() => {
    const apiKey = ORDERING_MAP[sortConfig.key];
    if (!apiKey) return '-start_date';
    return sortConfig.direction === 'desc' ? `-${apiKey}` : apiKey;
  }, [sortConfig]);

  const listQuery = useMemo(() => ({
    page_size: PAGE_SIZE,
    ordering,
    ...(debouncedSearch ? { search: debouncedSearch } : {}),
  }), [ordering, debouncedSearch]);

  const {
    items: projects,
    totalCount,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error,
  } = useProjects(listQuery);

  const hasActiveFilters = debouncedSearch.trim().length > 0;

  // Infinite scroll sentinel
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) fetchNextPage();
      },
      { threshold: 0.1 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const deleteProject = useDeleteProject();
  const deleteMutation = {
    isPending: deleteProject.isPending,
    mutate: (id) => deleteProject.mutate(id, {
      onSuccess: () => {
        setDeleteModal({ show: false, project: null });
        toast.success(t('projects.trash.movedToTrash'));
      },
      onError: () => toast.error(t('projects.errorDeleting')),
    }),
  };

  const bulkDeleteProject = useDeleteProject();
  const bulkDeleteMutation = {
    isPending: bulkDeleteProject.isPending,
    mutate: (ids) => Promise.all(ids.map((id) => bulkDeleteProject.mutateAsync(id))).then(
      () => {
        setSelectedKeys(new Set());
        setBulkDeleteModal(false);
        toast.success(t('projects.trash.movedToTrash'));
      },
      () => toast.error(t('projects.errorDeleting')),
    ),
  };

  const requestSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const handleSearchChange = (e) => setSearchTerm(e.target.value);

  const handleDeleteConfirm = () => deleteMutation.mutate(deleteModal.project.id);

  const resetFilters = () => setSearchTerm('');

  const hasActiveFiltersVisible = searchTerm.trim().length > 0;

  const columns = useMemo(() => [
    {
      key: 'name',
      label: t('projects.projectName'),
      sortable: true,
      render: (row) => <strong>{row.name}</strong>,
    },
    {
      key: 'customer',
      label: t('projects.customer'),
      sortable: true,
      render: (row) => row.customer_name || t('common.notSet'),
    },
    {
      key: 'start_date',
      label: t('projects.startDate'),
      sortable: true,
      render: (row) => row.start_date ? formatDate(row.start_date) : t('common.notSet'),
    },
    {
      key: 'end_date',
      label: t('projects.endDate'),
      sortable: true,
      render: (row) => row.end_date ? formatDate(row.end_date) : t('common.notSet'),
    },
    {
      key: 'status',
      label: t('projects.status'),
      sortable: true,
      render: (row) => (
        <StatusBadge color={row.status?.color} label={row.status?.name || t('common.notSet')} />
      ),
    },
    {
      key: 'actions',
      label: t('common.actions'),
      render: (row) => (
        <div className="btn-group btn-group-sm" onClick={(e) => e.stopPropagation()}>
          <AppButton
            as={Link}
            to={`/projects/detail/${row.id}`}
            variant="outline-secondary"
            size="sm"
            icon={<i className="ti ti-eye" aria-hidden="true" />}
            title={t('projects.actions.view')}
          />
          {canManage && (
            <AppButton
              as={Link}
              to={`/projects/edit/${row.id}`}
              variant="outline-secondary"
              size="sm"
              icon={<i className="ti ti-edit" aria-hidden="true" />}
              title={t('projects.actions.edit')}
            />
          )}
          {canManage && (
            <AppButton
              variant="outline-danger"
              size="sm"
              icon={<i className="ti ti-trash" aria-hidden="true" />}
              title={t('projects.trash.moveToTrash')}
              onClick={(e) => { e.stopPropagation(); setDeleteModal({ show: true, project: row }); }}
            />
          )}
        </div>
      ),
    },
  ], [t, canManage]);

  if (isLoading) {
    return (
      <PageShell
        pretitle={t('projects.overview')}
        title={t('projects.title')}
        description={t('projects.subtitle')}
      >
        <PageSection>
          <CenteredLoader />
        </PageSection>
      </PageShell>
    );
  }

  if (error) {
    return (
      <PageShell
        pretitle={t('projects.overview')}
        title={t('projects.title')}
        description={t('projects.subtitle')}
      >
        <Alert variant="danger" className="app-inline-alert">{t('projects.errorFetching')}</Alert>
      </PageShell>
    );
  }

  return (
    <>
      <PageShell
        pretitle={t('projects.overview')}
        title={t('projects.title')}
        description={t('projects.subtitle')}
        actions={(
          <div className="d-flex gap-2">
            {canManage && (
              <AppButton
                as={Link}
                to="/projects/trash"
                variant="outline-secondary"
                icon={<i className="ti ti-trash" aria-hidden="true" />}
              >
                {t('projects.trash.title')}
              </AppButton>
            )}
            {canManage && (
              <AppButton as={Link} to="/projects/create" variant="primary" icon={<IconPlus size={16} />}>
                {t('projects.newProject')}
              </AppButton>
            )}
          </div>
        )}
      >
        <PageSection
          title={t('projects.title')}
          description={t('projects.sectionDescription')}
          actions={
            <InputGroup className="app-search">
              <InputGroup.Text>
                <IconSearch size={16} />
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder={t('projects.searchPlaceholder')}
                value={searchTerm}
                onChange={handleSearchChange}
              />
            </InputGroup>
          }
          bodyClassName="p-0"
        >
          {selectedKeys.size > 0 && (
            <div className="d-flex align-items-center gap-2 px-3 py-2 border-bottom bg-primary-subtle">
              <span className="fw-medium text-primary-emphasis small">
                {selectedKeys.size} {t('common.selected')}
              </span>
              {canManage && (
                <AppButton
                  variant="outline-danger"
                  size="sm"
                  icon={<i className="ti ti-trash" aria-hidden="true" />}
                  onClick={() => setBulkDeleteModal(true)}
                >
                  {t('common.deleteSelected')}
                </AppButton>
              )}
              <button
                type="button"
                className="btn-close ms-auto"
                aria-label={t('common.clearSelection')}
                onClick={() => setSelectedKeys(new Set())}
              />
            </div>
          )}
          <AppTable
            columns={columns}
            data={projects}
            rowKey="id"
            loading={isLoading}
            sortKey={sortConfig.key}
            sortOrder={sortConfig.direction}
            onSort={requestSort}
            onRowClick={(row) => navigate(`/projects/detail/${row.id}`)}
            selectable
            selectedKeys={selectedKeys}
            onSelectionChange={setSelectedKeys}
            emptyTitle={hasActiveFiltersVisible ? t('projects.noResultsTitle') : t('projects.noProjects')}
            emptyMessage={hasActiveFiltersVisible ? t('projects.noResultsMessage') : t('projects.emptyMessage')}
            emptyAction={hasActiveFiltersVisible ? (
              <AppButton variant="outline-secondary" onClick={resetFilters}>
                {t('projects.resetFilters')}
              </AppButton>
            ) : canManage ? (
              <AppButton as={Link} to="/projects/create" variant="primary" icon={<IconPlus size={16} />}>
                {t('projects.newProject')}
              </AppButton>
            ) : undefined}
          />
          {/* Infinite scroll sentinel */}
          <div ref={sentinelRef} className="py-1" />
          {isFetchingNextPage && (
            <div className="d-flex justify-content-center py-3">
              <div className="spinner-border spinner-border-sm text-secondary" role="status">
                <span className="visually-hidden">Loading…</span>
              </div>
            </div>
          )}
          {!hasNextPage && projects.length > 0 && (
            <div className="text-center text-muted small py-2">
              {projects.length} / {totalCount}
            </div>
          )}
        </PageSection>
      </PageShell>

      <Modal show={deleteModal.show} onHide={() => setDeleteModal({ show: false, project: null })} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t('projects.trash.moveToTrash')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t('projects.trash.moveToTrashConfirm', { name: deleteModal.project?.name })}
        </Modal.Body>
        <Modal.Footer>
          <AppButton variant="outline-secondary" onClick={() => setDeleteModal({ show: false, project: null })}>
            {t('common.cancel')}
          </AppButton>
          <AppButton variant="danger" onClick={handleDeleteConfirm} loading={deleteMutation.isPending}>
            {t('projects.trash.moveToTrash')}
          </AppButton>
        </Modal.Footer>
      </Modal>

      <Modal show={bulkDeleteModal} onHide={() => setBulkDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t('projects.trash.moveToTrash')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t('projects.trash.moveToTrashConfirm', { name: `${selectedKeys.size} ${t('common.selected')}` })}
        </Modal.Body>
        <Modal.Footer>
          <AppButton variant="outline-secondary" onClick={() => setBulkDeleteModal(false)}>
            {t('common.cancel')}
          </AppButton>
          <AppButton
            variant="danger"
            onClick={() => bulkDeleteMutation.mutate(Array.from(selectedKeys))}
            loading={bulkDeleteMutation.isPending}
          >
            {t('projects.trash.moveToTrash')} ({selectedKeys.size})
          </AppButton>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default Projects;
